import { reservesSchema } from './contract';
import { SALE_CHANNELS } from './legal';
import { z } from 'zod';
import { DOSSIER_STATUSES } from './states/dossier';

/** Empty strings from forms are stored as NULL. */
const optText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));

// Accepts any RFC 4122 layout (incl. v7); stricter than a plain string, lenient on version.
const uuid = z
  .string()
  .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'UUID invalide');

export const clientKind = z.enum(['individual', 'company']);
export const civility = z.enum(['M', 'MME', 'AUTRE']);

export const clientFields = z.object({
  kind: clientKind,
  civility: civility.nullish().transform((v) => v ?? null),
  firstName: optText(100),
  lastName: optText(100),
  companyName: optText(200),
  siret: z
    .string()
    .trim()
    .regex(/^\d{14}$/, 'SIRET : 14 chiffres')
    .nullish()
    .or(z.literal(''))
    .transform((v) => (v ? v : null)),
  email: z.preprocess(
    (v) => (typeof v === 'string' ? v.trim().toLowerCase() || null : v),
    z
      .email('E-mail invalide')
      .nullish()
      .transform((v) => v ?? null),
  ),
  phone: optText(30),
  phoneAlt: optText(30),
  addressLine1: optText(200),
  addressLine2: optText(200),
  postalCode: optText(10),
  city: optText(100),
  notes: optText(4000),
  marketingConsent: z.boolean().default(false),
  /** professionnel : personne à contacter */
  contactName: optText(200),
  /** « Ajouter un numéro » : numéros supplémentaires */
  extraPhones: z
    .array(
      z.object({
        label: z
          .string()
          .trim()
          .max(50)
          .nullish()
          .transform((v) => v || null),
        number: z.string().trim().min(4).max(30),
      }),
    )
    .max(5)
    .default([]),
  /** client d'un partenaire (sous-traitance de pose) : référentiel « partner » ; null = client de l'entreprise */
  partnerId: uuid.nullish().transform((v) => v ?? null),
  /** champs métier configurables (définis par l'administrateur) */
  customFields: z
    .record(z.string().max(50), z.union([z.string().max(500), z.number(), z.boolean(), z.null()]))
    .default({}),
});

export type ClientFields = z.infer<typeof clientFields>;

/** Cross-field business rules for a client (checked on create and after merges). */
export function clientBusinessErrors(c: Partial<ClientFields>): Record<string, string> {
  const errors: Record<string, string> = {};
  if (c.kind === 'individual' && !c.lastName) errors.lastName = 'Nom obligatoire';
  if (c.kind === 'company' && !c.companyName) errors.companyName = 'Raison sociale obligatoire';
  if (!c.phone && !c.email) errors.phone = 'Téléphone ou e-mail obligatoire';
  return errors;
}

export const clientCreateInput = clientFields.extend({ id: uuid, territoryId: uuid });
export type ClientCreateInput = z.input<typeof clientCreateInput>;

export const CLIENT_MUTABLE_FIELDS = [
  'partnerId',
  'kind',
  'civility',
  'firstName',
  'lastName',
  'companyName',
  'siret',
  'email',
  'phone',
  'phoneAlt',
  'addressLine1',
  'addressLine2',
  'postalCode',
  'city',
  'notes',
  'marketingConsent',
  'contactName',
  'extraPhones',
  'customFields',
] as const satisfies readonly (keyof ClientFields)[];

const optNum = (min: number, max: number) =>
  z
    .number()
    .min(min)
    .max(max)
    .nullish()
    .transform((v) => (v === undefined ? null : v));

export const installAccess = z.enum(['none', 'ladder', 'nacelle']);

export const dossierFields = z.object({
  dossierTypeId: uuid,
  /** libellé libre facultatif (sinon le type du dossier sert de libellé) */
  title: optText(200),
  description: optText(8000),
  // adresse d'intervention / livraison si distincte de l'adresse de facturation du client
  siteAddressLine1: optText(200),
  siteAddressLine2: optText(200),
  sitePostalCode: optText(10),
  siteCity: optText(100),
  // « Je suis chez le client » : capture ponctuelle, avec consentement, jamais de suivi
  geoLat: optNum(-90, 90),
  geoLng: optNum(-180, 180),
  geoAccuracyM: optNum(0, 100_000),
  geoCapturedAt: z
    .string()
    .datetime()
    .nullish()
    .transform((v) => v ?? null),
  // conditions d'installation
  installWallTypeId: uuid.nullish().transform((v) => v ?? null),
  installAtHeight: z
    .boolean()
    .nullish()
    .transform((v) => v ?? null),
  installAccess: installAccess.nullish().transform((v) => v ?? null),
  installHeightM: optNum(0, 100),
  // réserves / décharges choisies parmi les modèles administrables (§4.11)
  reserves: reservesSchema.nullish().transform((v) => v ?? []),
  // conditions de la vente (rétractation, pack juridique) : par défaut, vente chez le client
  saleChannel: z
    .enum(SALE_CHANNELS)
    .nullish()
    .transform((v) => v ?? 'off_premises'),
  proEmployeeCount: z
    .number()
    .int()
    .min(0)
    .max(1_000_000)
    .nullish()
    .transform((v) => v ?? null),
  proOutsideMainActivity: z
    .boolean()
    .nullish()
    .transform((v) => v ?? null),
  /** le client demande que la prestation commence avant la fin du délai de rétractation (C5) */
  earlyStartRequested: z
    .boolean()
    .nullish()
    .transform((v) => v ?? false),
});
export type DossierFields = z.infer<typeof dossierFields>;

export const dossierCreateInput = dossierFields.extend({
  id: uuid,
  clientId: uuid,
  /** étape 1 : type + territoire (défaut : territoire du client) */
  territoryId: uuid.optional(),
  /**
   * chantier pour un partenaire (sous-traitance de pose, sans vente) ; absent = partenaire du client ;
   * fixé à la création
   */
  partnerId: uuid.nullish(),
});
export type DossierCreateInput = z.input<typeof dossierCreateInput>;

export const DOSSIER_MUTABLE_FIELDS = [
  'dossierTypeId',
  'title',
  'description',
  'siteAddressLine1',
  'siteAddressLine2',
  'sitePostalCode',
  'siteCity',
  'geoLat',
  'geoLng',
  'geoAccuracyM',
  'geoCapturedAt',
  'installWallTypeId',
  'installAtHeight',
  'installAccess',
  'installHeightM',
  'reserves',
  'saleChannel',
  'proEmployeeCount',
  'proOutsideMainActivity',
  'earlyStartRequested',
] as const satisfies readonly (keyof DossierFields)[];

/**
 * Completeness required to submit a dossier to control. Later modules add their own checks
 * (lignes de catalogue, signature, paiement) through the server-side transition guards.
 */
export function dossierSubmitErrors(
  d: Partial<DossierFields>,
  client:
    | {
        phone?: string | null;
        email?: string | null;
        addressLine1?: string | null;
        postalCode?: string | null;
        city?: string | null;
      }
    | undefined,
  type: { settings?: Record<string, unknown> } | undefined,
  /** nombre de lignes produits / prestations du dossier */
  lineCount: number,
  /** codes des catégories de photos présentes sur le dossier (liste « photo_category ») */
  photoCategoryCodes: readonly string[] = [],
  /** libellé d'une catégorie (pour un message lisible) */
  labelOf: (code: string) => string = (c) => c,
  /** une signature valide du client existe */
  signed = false,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!client) return { clientId: 'Client introuvable' };
  // §4.6 : produits / prestations du catalogue (désactivable par type : settings.requiresLines = false)
  if (type?.settings?.requiresLines !== false && lineCount < 1)
    errors.lines = 'Ajoutez au moins un produit ou une prestation';
  if (!client.phone) errors.phone = 'Téléphone du client obligatoire';
  const hasSite = !!(d.siteAddressLine1 && d.sitePostalCode && d.siteCity);
  const hasBilling = !!(client.addressLine1 && client.postalCode && client.city);
  if (!hasSite && !hasBilling) errors.siteAddressLine1 = 'Adresse d’intervention obligatoire';
  if (type?.settings?.requiresInstallConditions && !d.installWallTypeId)
    errors.installWallTypeId = 'Type de mur / support obligatoire';
  // à CHAQUE dossier (installation, entretien…) : le lieu peut changer d'un dossier à l'autre pour un même client
  if (d.installAtHeight === null || d.installAtHeight === undefined)
    errors.installAtHeight = 'Préciser si l’intervention est en hauteur';
  if (d.installAtHeight && (!d.installAccess || d.installAccess === 'none'))
    errors.installAccess = 'Échelle ou nacelle ?';
  if (d.installAtHeight && !d.installHeightM) errors.installHeightM = 'Hauteur approximative obligatoire';
  // §4.8 : photos obligatoires par type de dossier (settings.requiredPhotoCategories = codes de la liste)
  const required = Array.isArray(type?.settings?.requiredPhotoCategories)
    ? (type!.settings!.requiredPhotoCategories as string[])
    : [];
  const missing = required.filter((c) => !photoCategoryCodes.includes(c));
  if (missing.length) errors.photos = `Photo(s) manquante(s) : ${missing.map(labelOf).join(', ')}`;
  // §4.13 : signature du client exigée par type (settings.requiresSignature)
  if (type?.settings?.requiresSignature && !signed) errors.signature = 'Signature du client obligatoire';
  return errors;
}

export const commentCreateInput = z.object({
  id: uuid,
  dossierId: uuid,
  /** public = visible aux intervenants autorisés ; private = administrateur uniquement */
  visibility: z.enum(['public', 'private']),
  body: z.string().trim().min(1, 'Commentaire vide').max(4000),
});
export type CommentCreateInput = z.input<typeof commentCreateInput>;

export const dossierStatus = z.enum(DOSSIER_STATUSES);

export const transitionInput = z.object({
  /** status the client believed the dossier was in (optimistic concurrency on status) */
  from: dossierStatus,
  to: dossierStatus,
  reasonId: uuid.nullish().transform((v) => v ?? null),
  comment: optText(2000),
});
export type TransitionInput = z.input<typeof transitionInput>;

// Phase 1 : affectation du commercial (et technicien provisoire en attendant le module Planning).
export const assignmentRole = z.enum(['commercial', 'technician']);
export type AssignmentRole = z.infer<typeof assignmentRole>;

export const assignInput = z.object({ userId: uuid, role: assignmentRole });

/** A field change carries the value the client based its edit on (3-way merge). */
export const fieldChange = z.object({ from: z.unknown(), to: z.unknown() });
export const changesSchema = z.record(z.string(), fieldChange);
export type FieldChanges = Record<string, { from: unknown; to: unknown }>;

export const loginInput = z.object({
  tenant: z.string().trim().toLowerCase().min(1),
  email: z.preprocess((v) => (typeof v === 'string' ? v.trim().toLowerCase() : v), z.email()),
  password: z.string().min(1).max(200),
  deviceId: z.string().trim().min(8).max(100).optional(),
  deviceName: z.string().trim().max(100).optional(),
  platform: z.enum(['web', 'ios', 'android', 'other']).default('other'),
});

export const PASSWORD_MIN_LENGTH = 12;
export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Au moins ${PASSWORD_MIN_LENGTH} caractères`)
  .max(200)
  .refine(
    (v) => /[a-z]/.test(v) && /[A-Z]/.test(v) && /\d/.test(v),
    'Majuscule, minuscule et chiffre requis',
  );

export { uuid as uuidSchema };

/* ------------------------------------------------------------------ champs métier configurables */

/** Défini par l'administrateur de l'entreprise (paramètres tenant `clientCustomFields`). */
export const customFieldDefinition = z.object({
  code: z.string().regex(/^[a-z0-9_]{2,50}$/),
  label: z.string().trim().min(1).max(100),
  type: z.enum(['text', 'number', 'boolean', 'select']),
  options: z.array(z.string().trim().min(1).max(100)).max(50).optional(),
  appliesTo: z.enum(['individual', 'company', 'all']).default('company'),
  required: z.boolean().default(false),
  isActive: z.boolean().default(true),
});
export type CustomFieldDefinition = z.output<typeof customFieldDefinition>;

export function customFieldErrors(
  defs: CustomFieldDefinition[],
  kind: 'individual' | 'company',
  values: Record<string, unknown>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const active = defs.filter((d) => d.isActive && (d.appliesTo === 'all' || d.appliesTo === kind));
  for (const key of Object.keys(values)) {
    if (!defs.some((d) => d.code === key)) errors[`customFields.${key}`] = 'Champ inconnu';
  }
  for (const d of active) {
    const v = values[d.code];
    const empty = v === undefined || v === null || v === '';
    if (empty) {
      if (d.required) errors[`customFields.${d.code}`] = `${d.label} obligatoire`;
      continue;
    }
    const ok =
      (d.type === 'text' && typeof v === 'string') ||
      (d.type === 'number' && typeof v === 'number' && Number.isFinite(v)) ||
      (d.type === 'boolean' && typeof v === 'boolean') ||
      (d.type === 'select' && typeof v === 'string' && (d.options ?? []).includes(v));
    if (!ok) errors[`customFields.${d.code}`] = `${d.label} : valeur invalide`;
  }
  return errors;
}

/* ------------------------------------------------------------------ récupération de compte & double vérification */

export const forgotPasswordInput = z.object({
  tenant: z.string().trim().toLowerCase().min(1).max(60),
  email: z.preprocess((v) => (typeof v === 'string' ? v.trim().toLowerCase() : v), z.email()),
});
export const resetPasswordInput = z.object({
  token: z.string().trim().min(40).max(200),
  password: passwordSchema,
});
export const mfaCodeInput = z.object({ code: z.string().trim().min(6).max(20) });
export const mfaDisableInput = z.object({
  password: z.string().min(1).max(200),
  code: z.string().trim().min(6).max(20),
});

/** Les comptes « administrateurs » visés par l’obligation de double vérification (paramètre de l’entreprise). */
export const MFA_ADMIN_PERMISSIONS = ['tenant.settings.manage', 'user.manage', 'user.assign_roles'] as const;
export const isMfaAdmin = (perms: Record<string, unknown>) => MFA_ADMIN_PERMISSIONS.some((p) => p in perms);

/** Modification d'une signature par l'administrateur (nom et qualité du signataire). */
export const signatureEditInput = z
  .object({
    signerName: z.string().trim().min(2).max(200).optional(),
    signerQuality: z.enum(['client', 'representative']).optional(),
  })
  .refine((v) => v.signerName !== undefined || v.signerQuality !== undefined, 'Rien à modifier');
export const signatureDeleteInput = z.object({
  reason: z.string().trim().min(3, 'Motif obligatoire').max(500),
});

/* ------------------------------------------------------------------ territoire déduit automatiquement */

/**
 * Territoire d'un nouveau client / dossier, sans le demander à l'utilisateur : un seul territoire autorisé → celui-là ;
 * sinon celui dont un préfixe de code postal correspond (971 → Guadeloupe…) ; sinon le territoire choisi dans l'en-tête.
 * `null` seulement si rien ne permet de trancher (le formulaire demande alors le territoire).
 */
export function guessTerritory<T extends { id: string; settings: Record<string, unknown> }>(
  allowed: readonly T[],
  postalCode: string | null | undefined,
  preferredId?: string | null,
): string | null {
  if (allowed.length === 1) return allowed[0]!.id;
  const cp = (postalCode ?? '').replace(/\s/g, '');
  if (cp) {
    const hit = allowed.filter((t) =>
      ((t.settings.postalCodePrefixes as string[] | undefined) ?? []).some((p) => cp.startsWith(p)),
    );
    if (hit.length === 1) return hit[0]!.id;
  }
  return preferredId && allowed.some((t) => t.id === preferredId) ? preferredId : null;
}

/** Chantier pour un partenaire (sous-traitance de pose) : pas de vente, planifié directement. */
export const isPartnerJob = (d: { partnerId?: string | null }) => !!d.partnerId;

/**
 * Avant l'envoi en planification d'un chantier partenaire : « en hauteur ? » répondu et au moins une prestation
 * (puissance de la climatisation : elle fixe la rémunération du poseur selon le barème du catalogue).
 */
export function partnerScheduleErrors(
  d: { installAtHeight?: boolean | null },
  lineCount: number,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (d.installAtHeight == null) errors.installAtHeight = 'Indiquez si l’intervention est en hauteur';
  if (!lineCount) errors.lines = 'Choisissez la puissance de la climatisation (au moins une prestation)';
  return errors;
}

/**
 * Affichage de champs facultatifs du dossier commercial, réglé par l'administrateur (paramètres de l'entreprise).
 *  - showSaleChannel : bloc « Contrat conclu » (hors établissement / à distance / en agence) ;
 *  - showProActivity : « Nombre de salariés » et « L'objet du contrat n'entre pas dans le champ de l'activité
 *    principale du client ».
 * Masqués par défaut ; masquer ne change ni n'efface les valeurs déjà enregistrées.
 */
export const dossierFormSettingsSchema = z.object({
  showSaleChannel: z.boolean().default(false),
  showProActivity: z.boolean().default(false),
});
export type DossierFormSettings = z.infer<typeof dossierFormSettingsSchema>;
export function dossierFormSettingsOf(settings: unknown): DossierFormSettings {
  const raw = (settings as { dossierForm?: unknown } | null | undefined)?.dossierForm;
  const parsed = dossierFormSettingsSchema.safeParse(raw ?? {});
  return parsed.success ? parsed.data : { showSaleChannel: false, showProActivity: false };
}
